import classNames from 'classnames'
import router from 'next/router'
import React, { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import OtpInput from 'react-otp-input'
import { useMutation } from 'react-query'
import Context from '../../utils/Context'
import { DiaperBankPublic } from '../../utils/DiaperBankDTO'
import { toastMessage } from '../toastMessage'
import Button from '../form/Button'
import FormFieldInput from '../form/FormFieldInput'
import { gaEvent } from '../../utils/GA'

export default function OTPVerification({ diaperBank, onClose }: { diaperBank: DiaperBankPublic; onClose?: () => void }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ phoneNumber: string }>()

  const [isCodeSent, setIsCodeSent] = useState<boolean>(false)
  const [codeSendError, setCodeSendError] = useState<boolean>(false)
  const [codeVerificationError, setCodeVerificationError] = useState<boolean>(false)

  const [otp, setOtp] = useState<string>()

  const context = useContext(Context)

  const { mutateAsync: sendCodeMutation, isLoading: isSendingCode } = useMutation((data: { phoneNumber: string }) =>
    context.apiClient.post<{ success: boolean }>(`diaperBanks/${diaperBank.slug}/verification`, data),
  )

  const { mutateAsync: verifyCodeMutation, isLoading: isVerifyingCode } = useMutation((data: { code: string }) =>
    context.apiClient.put<DiaperBankPublic>(`diaperBanks/${diaperBank.slug}/verification`, data),
  )

  const onSubmitPhone = async ({ phoneNumber }: { phoneNumber: string }) => {
    setOtp(undefined)
    setCodeSendError(false)
    setIsCodeSent(false)
    try {
      const response = await sendCodeMutation({ phoneNumber })
      setIsCodeSent(true)
    } catch (error: any) {
      console.error(error, error.response?.body)
      setCodeSendError(true)
    }
  }

  const handleOtpChange = (val: string) => setOtp(val)

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (otp?.length && otp.length < 6) return
    try {
      const result = await verifyCodeMutation({ code: otp! })
      if (result.data.isVerified) {
        toastMessage({ title: 'You are verified!', body: `${diaperBank.babyName}'s Diaper Bank is now verified and live!`, type: 'success' })
        gaEvent({ action: 'df_fund_verified' })
        if (onClose) onClose()
        router.push(`/bank/${result.data.slug}/`)
      } else {
        setCodeVerificationError(true)
      }
    } catch (error) {
      setCodeVerificationError(true)
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmitPhone)} className={classNames({ hidden: isCodeSent })}>
        <div className=''>
          <FormFieldInput
            label='Phone Number'
            errors={errors}
            type='text'
            register={register('phoneNumber', { required: true, pattern: { value: /^\d{10}$/, message: 'Must be a valid phone number' } })}
            helpText={`Please provide a valid ${diaperBank.store === 'hbca' ? 'Canada' : 'US'} cell phone number. Standard carrier charges may apply.`}
          />
          <Button className='mb-6' type='submit' fullWidth loading={isSendingCode} disabled={isSendingCode} color='primary'>
            Send Code
          </Button>

          {codeSendError && (
            <div className='bg-red-50 p-4 font-light text-red-800 mt-6'>There was an error sending the code. Please check that the number is correct and try again</div>
          )}
        </div>
      </form>

      {isCodeSent && (
        <div className='form-fieldset'>
          <div className='font-light'>
            Please enter the code you received. The code is valid for 5 minutes. If you need to, you can{' '}
            <button type='button' onClick={() => setIsCodeSent(false)} className='text-wisteria'>
              resend Code
            </button>
          </div>

          <form onSubmit={verifyCode}>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              isInputNum
              inputStyle='otp-input'
              focusStyle={'border-gray-500'}
              containerStyle='otp-container'
              shouldAutoFocus
            />

            <Button className='my-6' type='submit' fullWidth loading={isVerifyingCode} disabled={isVerifyingCode || (!!otp && otp.length < 6)} color='primary'>
              Verify Code
            </Button>
          </form>

          {codeVerificationError && (
            <div className='bg-red-50 p-4 font-light text-red-800 mt-6'>
              Your code couldn't be verified. Please try{' '}
              <button type='button' onClick={() => setIsCodeSent(false)} className='text-wisteria'>
                resending the code.
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
